/**
 * 3D Room Visualizer — export of the room for rendering elsewhere (Blender).
 *
 * The room as the viewer has built it — walls, ceiling, floor, openings, trim,
 * accessories, props — goes out as a binary glTF (.glb) in the room's own
 * coordinates (feet; the room group's offset included, so the floor's front-
 * left corner is at (−L/2, 0, −W/2) as in the viewer). Every mesh carries a
 * `render` tag in its extras saying what it is (board, SlatWall, glass, trim,
 * lumber, a prop …) and its product colour, so the renderer can put its own
 * materials on: the browser's materials go out as flat colours only (their
 * maps are the browser's look, not the product's).
 *
 * Beside it a MANIFEST (JSON): the room's size, the camera as the viewer has
 * it (position, target, field of view), the lamps where the viewer hangs them
 * with their warmth and brightness, and the ambient level — enough for a
 * renderer to set up a scene that matches the view.
 *
 * Pure three.js: no WebGL, no DOM beyond Blob/FileReader (jsdom has both), so
 * it runs under test as well as in the browser.
 */

import * as THREE from 'three';
import type { PhotoRenderSettings } from './photoRender';

export type RenderKind = 'wcb' | 'slatwall' | 'glass' | 'seam' | 'trim' | 'lumber' | 'doorWood' | 'opening' | 'accessory' | 'prop' | 'fixture' | 'floor' | 'drywall' | 'slab' | 'lawn' | 'fence' | 'frame' | 'hardware' | 'other';

export interface RenderManifest {
  version: 1;
  units: 'feet';
  room: { length: number; width: number; height: number; peak: number };
  camera: { position: [number, number, number]; target: [number, number, number]; fov: number; aspect: number };
  lamps: { position: [number, number, number]; kelvin: number; intensity: number; fixture: string }[];
  ambient: number;
  /** The look's settings that matter to a renderer. */
  look: {
    warmth: number; intensity: number; ambient: number; environment: string; exposure: number;
    /** How dark the joints between boards read (0 hides them): the renderer's joint shading follows it like the browser's seam strips do. */
    seamStrength: number;
  };
  /**
   * Where the 360 is taken from (panorama.ts): the room's centre at eye
   * height, looking level at the middle of `face`. Same frame as `camera`.
   * Absent in older exports (the renderer then takes the room's centre, facing the front wall).
   */
  panorama?: { position: [number, number, number]; target: [number, number, number]; face: string };
  /** How Blender renders it: the Photoreal render window's settings (photoRender.ts; scripts/blender/RENDER-SETTINGS.md). Absent in older exports. */
  render?: PhotoRenderSettings;
  /** The room's space type (presets.ts SPACE_TYPES id): the server renders it with one of that space's render looks (renderLooks.ts). */
  space?: string;
  /** Which render look the server put in `render` (render-jobs-lib.php rj_apply_look); absent when none did. */
  renderLook?: { space: string; id: string; name: string };
}

/** What a mesh is, from where the viewer put it and what it is made of. */
function kindOf(mesh: THREE.Mesh): RenderKind {
  const material = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.Material | undefined;
  const mk = material?.userData?.kind as string | undefined;
  if (mk === 'glass') return 'glass';
  if (mk === 'seam') return 'seam';
  if (mk === 'lumber') return 'lumber';
  if (mk === 'doorWood') return 'doorWood';
  if (mk === 'drywall') return 'drywall'; // a bare area's sheet (bareFill.ts): drywall, its paper back, or the grid
  // The yard (yardBuilder.ts): decided here, before the rotated-plane-at-the-floor rule below would call a slab a floor.
  if (mk === 'slab') return 'slab';
  if (mk === 'lawn') return 'lawn';
  if (mk === 'fence') return 'fence';
  // An opening's frames and sills, and its hardware (openingMeshBuilder.ts): decided here, before the ancestor walk would call them `opening`.
  if (mk === 'frame') return 'frame';
  if (mk === 'hardware') return 'hardware';
  // Up the tree: the groups the builders name.
  let o: THREE.Object3D | null = mesh;
  while (o) {
    const n = o.name || '';
    if (n.startsWith('slatwall-extruded')) return 'slatwall';
    if (n.startsWith('wcb-extruded')) return 'wcb';
    if (n.startsWith('accessory-')) return 'accessory';
    if (n.startsWith('prop-') || n === 'staging') return 'prop';
    if (n === 'light-fixture') return 'fixture';
    if (n.startsWith('trim')) return 'trim';
    if (n.startsWith('openings') || n.startsWith('garage') || n.startsWith('door') || n.startsWith('window')) return 'opening';
    o = o.parent;
  }
  if (mk === 'product') return 'wcb';
  if (mesh.geometry instanceof THREE.PlaneGeometry && mesh.rotation.x !== 0 && mesh.position.y === 0) return 'floor';
  return 'other';
}

const hex = (c: THREE.Color | undefined) => (c ? `#${c.getHexString()}` : undefined);

/** The kinds whose picture (colour map) travels in the file; everything else goes out as its flat colour. */
const PICTURED: ReadonlySet<RenderKind> = new Set<RenderKind>(['floor', 'doorWood', 'lumber', 'drywall']);

/**
 * The browser cuts boards to shape with clipping planes (a notch round a
 * window, a wall's top under a sloped ceiling): three discards every fragment
 * on the negative side of any of the material's planes (world space). A glTF
 * has no such thing, so the cut is made in the geometry instead — each
 * triangle clipped to the planes' half-spaces (Sutherland–Hodgman), every
 * attribute interpolated along the cut edges. The mesh's own geometry is
 * untouched; the result is a new, non-indexed one in the same local space.
 */
export function clippedGeometry(mesh: THREE.Mesh, planesWorld: THREE.Plane[]): THREE.BufferGeometry {
  const src = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
  const inv = new THREE.Matrix4().copy(mesh.matrixWorld).invert();
  const planes = planesWorld.map((p) => p.clone().applyMatrix4(inv));
  const names = Object.keys(src.attributes);
  const attrs = names.map((n) => src.getAttribute(n) as THREE.BufferAttribute);
  const position = src.getAttribute('position') as THREE.BufferAttribute;
  type Vertex = { pos: THREE.Vector3; data: number[][] };
  const vertexAt = (i: number): Vertex => ({
    pos: new THREE.Vector3().fromBufferAttribute(position, i),
    data: attrs.map((a) => Array.from({ length: a.itemSize }, (_, k) => a.array[i * a.itemSize + k] as number)),
  });
  const between = (a: Vertex, b: Vertex, t: number): Vertex => ({
    pos: a.pos.clone().lerp(b.pos, t),
    data: a.data.map((va, j) => va.map((x, k) => x + (b.data[j][k] - x) * t)),
  });
  const out: number[][] = names.map(() => []);
  const emit = (v: Vertex) => v.data.forEach((d, j) => out[j].push(...d));
  for (let i = 0; i < position.count; i += 3) {
    let poly: Vertex[] = [vertexAt(i), vertexAt(i + 1), vertexAt(i + 2)];
    for (const plane of planes) {
      if (poly.length < 3) break;
      const next: Vertex[] = [];
      for (let k = 0; k < poly.length; k++) {
        const cur = poly[k], prev = poly[(k + poly.length - 1) % poly.length];
        const dc = plane.distanceToPoint(cur.pos), dp = plane.distanceToPoint(prev.pos);
        if (dc >= 0) {
          if (dp < 0) next.push(between(prev, cur, dp / (dp - dc)));
          next.push(cur);
        } else if (dp >= 0) {
          next.push(between(prev, cur, dp / (dp - dc)));
        }
      }
      poly = next;
    }
    for (let k = 1; k + 1 < poly.length; k++) { emit(poly[0]); emit(poly[k]); emit(poly[k + 1]); }
  }
  const geometry = new THREE.BufferGeometry();
  names.forEach((n, j) => geometry.setAttribute(n, new THREE.Float32BufferAttribute(out[j], attrs[j].itemSize)));
  return geometry;
}

/**
 * Tag every mesh under the roots and export them as one .glb. The materials go
 * out flat (no maps): each mesh's extras say what it is and its colour.
 */
export async function exportRoomGlb(roots: THREE.Object3D[], options: { exclude?: THREE.Object3D[] } = {}): Promise<ArrayBuffer> {
  const hidden: THREE.Object3D[] = [];
  for (const o of options.exclude ?? []) {
    if (o.visible) { o.visible = false; hidden.push(o); }
  }
  // What the viewer never draws, but that still counts as visible to the
  // exporter: a mesh whose material is invisible — the click targets round
  // a window's lift handle, a door's lever and the opener button
  // (openingMeshBuilder handleTarget). Swapped for a flat stand-in below,
  // each would come out as a solid box in the render (Rob's sills, 2026-10-08).
  for (const root of roots) {
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || !mesh.visible) return;
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      if (mesh.userData.openerHandle || materials.every((m) => m && m.visible === false)) {
        mesh.visible = false;
        hidden.push(mesh);
      }
    });
  }
  // Flat stand-ins for the browser's materials, and cut geometry for what its
  // clipping planes shape — both restored after.
  const swapped: { mesh: THREE.Mesh; material: THREE.Material | THREE.Material[] }[] = [];
  const cut: { mesh: THREE.Mesh; geometry: THREE.BufferGeometry }[] = [];
  for (const root of roots) {
    root.updateMatrixWorld(true);
    root.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh || !mesh.visible) return;
      const kind = kindOf(mesh);
      const first = (Array.isArray(mesh.material) ? mesh.material[0] : mesh.material) as THREE.MeshStandardMaterial;
      // A textured sheet's own tone (bareFill.ts) stands in for its white-under-the-picture colour.
      const exportColor = typeof first?.userData?.exportColor === 'string' ? first.userData.exportColor : hex(first?.color);
      mesh.userData.render = { kind, color: exportColor, opacity: first?.opacity ?? 1, ...(typeof first?.userData?.style === 'string' ? { style: first.userData.style } : {}) };
      if (first?.clippingPlanes?.length) {
        cut.push({ mesh, geometry: mesh.geometry });
        mesh.geometry = clippedGeometry(mesh, first.clippingPlanes);
      }
      const flat = new THREE.MeshStandardMaterial({
        color: first?.color ?? new THREE.Color(0xffffff),
        roughness: first?.roughness ?? 0.6,
        metalness: first?.metalness ?? 0,
        transparent: kind === 'glass',
        opacity: kind === 'glass' ? 0.3 : 1,
        vertexColors: first?.vertexColors ?? false,
        // A sheet the viewer draws from both sides (a bare area's) goes out
        // `doubleSided`, so the renderer does not cull it from behind.
        side: first?.side ?? THREE.FrontSide,
        // The floor's picture is the look that was chosen for it (the render
        // settings' floor), and the wood grain on a wooden door or the framing
        // is the look of that style: both go with the file, tiling and all
        // (KHR_texture_transform), so the render shows what the browser did.
        // The product surfaces' bump maps stay behind — the renderer has its
        // own, and makes the wood's relief from the grain picture itself.
        map: PICTURED.has(kind) ? first?.map ?? null : null,
      });
      flat.name = `${kind}:${hex(first?.color) ?? ''}`;
      swapped.push({ mesh, material: mesh.material });
      mesh.material = flat;
    });
  }
  const restore = () => {
    for (const { mesh, material } of swapped) mesh.material = material;
    for (const { mesh, geometry } of cut) { mesh.geometry.dispose(); mesh.geometry = geometry; }
    for (const o of hidden) o.visible = true;
  };
  try {
    // Loaded on demand: the exporter is big and only wanted on a click — and
    // it is an ES module the test runner does not transform, so modules that
    // import this one stay loadable under test.
    const { GLTFExporter } = await import('three/examples/jsm/exporters/GLTFExporter.js');
    const exporter = new GLTFExporter();
    const writing = exporter.parseAsync(roots, { binary: true, onlyVisible: true, includeCustomExtensions: false });
    // The exporter has read the whole scene by the time parseAsync returns
    // (only the image encoding and the final packing are still to come), so
    // the browser's own materials go back on NOW: a frame drawn in the
    // meantime with the stand-ins on would trip the render pipeline (its
    // glass pass expects its glass).
    restore();
    return (await writing) as ArrayBuffer;
  } catch (error) {
    restore();
    throw error;
  }
}

/** The camera as a renderer needs it: where it is, what it looks at, its field of view. */
export function cameraManifest(camera: THREE.PerspectiveCamera, aspect: number): RenderManifest['camera'] {
  camera.updateMatrixWorld();
  const position = new THREE.Vector3().setFromMatrixPosition(camera.matrixWorld);
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.getWorldQuaternion(new THREE.Quaternion()));
  const target = position.clone().addScaledVector(forward, 10);
  return { position: position.toArray() as [number, number, number], target: target.toArray() as [number, number, number], fov: camera.fov, aspect };
}

/** The .glb and manifest as files to save. */
export function renderExportFiles(glb: ArrayBuffer, manifest: RenderManifest, stem = 'room'): { name: string; blob: Blob }[] {
  return [
    { name: `${stem}.glb`, blob: new Blob([glb], { type: 'model/gltf-binary' }) },
    { name: `${stem}.render.json`, blob: new Blob([JSON.stringify(manifest, null, 2)], { type: 'application/json' }) },
  ];
}
